export interface LocalFile {
  id: string;
  name: string;
  type: string;
  size: number;
  lastModified: number;
  file: File;
  path: string;
  webViewLink?: string;
}

class LocalFileService {
  private static instance: LocalFileService;

  private constructor() {}

  static getInstance(): LocalFileService {
    if (!LocalFileService.instance) {
      LocalFileService.instance = new LocalFileService();
    }
    return LocalFileService.instance;
  }

  /**
   * ファイルのプレビューURLを生成
   * 画像ファイルの場合はブラウザで直接表示可能
   */
  async createPreviewUrl(file: File): Promise<string | null> {
    try {
      // 画像ファイルの場合
      if (file.type.startsWith('image/')) {
        return URL.createObjectURL(file);
      }

      // テキストファイルの場合
      if (file.type.startsWith('text/') || 
          file.name.endsWith('.txt') || 
          file.name.endsWith('.md') ||
          file.name.endsWith('.json') ||
          file.name.endsWith('.csv')) {
        return URL.createObjectURL(file);
      }

      // PDFの場合
      if (file.type === 'application/pdf') {
        return URL.createObjectURL(file);
      }

      // その他のファイルはプレビュー不可
      return null;
    } catch (error) {
      console.error('Error creating preview URL:', error);
      return null;
    }
  }

  /**
   * ファイルの内容を読み取る（テキストファイルのみ）
   */
  async readFileContent(file: File): Promise<string | null> {
    try {
      // テキスト系のファイルのみ読み取り
      if (file.type.startsWith('text/') || 
          file.name.endsWith('.txt') || 
          file.name.endsWith('.md') ||
          file.name.endsWith('.json') ||
          file.name.endsWith('.csv')) {
        return await file.text();
      }
      return null;
    } catch (error) {
      console.error('Error reading file content:', error);
      return null;
    }
  }

  /**
   * CSVファイルをパース
   */
  async parseCSV(file: File): Promise<string[][]> {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      return lines.map(line => {
        // 簡易的なCSVパース（カンマ区切り）
        // より複雑なCSVの場合は専用ライブラリを使用
        return line.split(',').map(cell => cell.trim());
      });
    } catch (error) {
      console.error('Error parsing CSV:', error);
      throw error;
    }
  }

  /**
   * ファイルのメタデータを取得
   */
  getFileMetadata(file: File): {
    extension: string;
    isImage: boolean;
    isText: boolean;
    isPDF: boolean;
    isCSV: boolean;
  } {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    return {
      extension,
      isImage: file.type.startsWith('image/'),
      isText: file.type.startsWith('text/') || ['txt', 'md', 'json', 'csv'].includes(extension),
      isPDF: file.type === 'application/pdf',
      isCSV: file.type === 'text/csv' || extension === 'csv',
    };
  }

  /**
   * プレビューURLをクリーンアップ
   */
  revokePreviewUrl(url: string): void {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }
}

export default LocalFileService;